(() => {
  if (customElements.get('product-shipping-calculator')) return;

  const STATE_NAMES = {
    AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará',
    DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão', MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul', MG: 'Minas Gerais', PA: 'Pará', PB: 'Paraíba', PR: 'Paraná',
    PE: 'Pernambuco', PI: 'Piauí', RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul', RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina',
    SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins'
  };

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, options);
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = data.description || data.message || data.error || 'Não foi possível calcular o frete.';
      throw new Error(message);
    }

    return data;
  };

  const cleanupStaleQuoteItems = async () => {
    const cart = await requestJson(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    const staleItems = cart.items.filter((item) => item.properties?._isola_shipping_quote);

    for (const item of staleItems) {
      await requestJson(`${rootUrl()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 })
      });
    }
  };

  class ProductShippingCalculator extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.productFormId);
      this.zipInput = this.querySelector('[data-shipping-zip]');
      this.submitButton = this.querySelector('[data-shipping-submit]');
      this.status = this.querySelector('[data-shipping-status]');
      this.rates = this.querySelector('[data-shipping-rates]');
      this.currency = this.dataset.currency || 'BRL';
      this.busy = false;

      this.zipInput?.addEventListener('input', () => {
        const digits = this.zipInput.value.replace(/\D/g, '').slice(0, 8);
        this.zipInput.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
      });
      this.zipInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.calculate();
        }
      });
      this.submitButton?.addEventListener('click', () => this.calculate());

      if (!window.isolaShippingQuoteCleanup) {
        window.isolaShippingQuoteCleanup = cleanupStaleQuoteItems().catch(() => undefined);
      }
    }

    showStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = false;
      this.status.classList.toggle('is-error', isError);
    }

    clearResults() {
      if (this.rates) {
        this.rates.replaceChildren();
        this.rates.hidden = true;
      }
    }

    selectedItem() {
      const variantInput = this.form?.elements?.namedItem('id') || this.form?.querySelector('[name="id"]');
      const quantityInput = this.form?.elements?.namedItem('quantity') || this.form?.querySelector('[name="quantity"]');
      const variantId = Number(variantInput?.value);
      const quantity = Math.max(1, Number(quantityInput?.value) || 1);

      if (!variantId) throw new Error('Selecione uma opção disponível do produto.');
      return { variantId, quantity };
    }

    async findProvince(zip) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
        const address = await response.json();
        if (address.erro) throw new Error('CEP não encontrado.');
        return STATE_NAMES[address.uf] || address.uf || '';
      } catch (error) {
        if (error.message === 'CEP não encontrado.') throw error;
        return '';
      }
    }

    async removeTemporaryItem(key) {
      if (!key) return;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          await requestJson(`${rootUrl()}cart/change.js`, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
          return;
        } catch (error) {
          if (attempt === 1) throw error;
        }
      }
    }

    deliveryLabel(rate) {
      const days = rate.delivery_days;
      if (!Array.isArray(days) || days.length === 0) return '';

      const minimum = days[0];
      const maximum = days.length > 1 ? days[days.length - 1] : minimum;
      if (minimum === maximum) return minimum === 1 ? '1 dia útil' : `${minimum} dias úteis`;
      return `${minimum} a ${maximum} dias úteis`;
    }

    renderRates(shippingRates) {
      this.clearResults();
      const formatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: this.currency });

      shippingRates.forEach((rate) => {
        const item = document.createElement('li');
        item.className = 'isola-product-shipping__rate';

        const details = document.createElement('span');
        details.className = 'isola-product-shipping__rate-details';

        const name = document.createElement('strong');
        name.textContent = rate.name;
        details.appendChild(name);

        const delivery = this.deliveryLabel(rate);
        if (delivery) {
          const estimate = document.createElement('small');
          estimate.textContent = `Prazo estimado: ${delivery}`;
          details.appendChild(estimate);
        }

        const price = document.createElement('strong');
        price.className = 'isola-product-shipping__rate-price';
        price.textContent = formatter.format(Number(rate.price) || 0);

        item.append(details, price);
        this.rates.appendChild(item);
      });

      this.rates.hidden = false;
    }

    async calculate() {
      if (this.busy) return;

      const zip = this.zipInput?.value.replace(/\D/g, '') || '';
      if (zip.length !== 8) {
        this.clearResults();
        this.showStatus('Digite um CEP válido com 8 números.', true);
        this.zipInput?.focus();
        return;
      }

      let temporaryKey = '';
      this.busy = true;
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Calculando...';
      this.clearResults();
      this.showStatus('Consultando valores e prazos...');

      try {
        await window.isolaShippingQuoteCleanup;
        const { variantId, quantity } = this.selectedItem();
        const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const province = await this.findProvince(zip);

        const added = await requestJson(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity,
              properties: { _isola_shipping_quote: token }
            }]
          })
        });
        temporaryKey = added.items?.find((item) => item.properties?._isola_shipping_quote === token)?.key || '';

        const query = new URLSearchParams({
          'shipping_address[zip]': zip,
          'shipping_address[country]': 'Brazil',
          'shipping_address[province]': province
        });
        const data = await requestJson(`${rootUrl()}cart/shipping_rates.json?${query.toString()}`, {
          headers: { Accept: 'application/json' }
        });

        if (!data.shipping_rates?.length) {
          throw new Error('Não encontramos uma opção de frete para este CEP.');
        }

        this.renderRates(data.shipping_rates);
        this.showStatus('Opcoes de entrega para o CEP informado:');
      } catch (error) {
        this.clearResults();
        this.showStatus(error.message || 'Não foi possível calcular o frete. Tente novamente.', true);
      } finally {
        try {
          await this.removeTemporaryItem(temporaryKey);
        } catch (error) {
          window.isolaShippingQuoteCleanup = cleanupStaleQuoteItems().catch(() => undefined);
        }
        this.busy = false;
        this.submitButton.disabled = false;
        this.submitButton.textContent = 'Calcular';
      }
    }
  }

  customElements.define('product-shipping-calculator', ProductShippingCalculator);
})();
